import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import CustomerDetailsForm from "./components/CustomerDetailsForm";
import ValidationPanel from "./components/ValidationPanel";
import RoofInspectionSection from "./components/RoofInspectionSection";
import SitePhotoCapture from "./components/SitePhotoCapture";
import QRCodeLinking from "./components/QRCodeLinking";
import SurveyDetailsForm from "./components/SurveyDetailsForm";
import RoofEngineeringForm from "./components/RoofEngineeringForm";
import ElectricalLogisticsForm from "./components/ElectricalLogisticsForm";
import DocumentPhotosForm from "./components/DocumentPhotosForm";
import ObstaclesSignoffForm from "./components/ObstaclesSignoffForm";
import {
  customers as customersAPI,
  surveys as surveysAPI,
  clusters as clustersAPI,
  regions as regionsAPI,
  staffAssignments as staffAssignmentsAPI,
  notifications as notificationsAPI,
  appSettings as appSettingsAPI,
} from "../../lib/database";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";

// Normalize API survey to payload shape (for lastSavedSurveyRef). No component state.
function normalizeSurveyFromApi(apiSurvey) {
  if (!apiSurvey) return null;
  const existingUrls = (arr) =>
    (Array.isArray(arr) ? arr : []).filter((p) => typeof p === "string");
  const existingUrl = (v) => (v && typeof v === "string" ? v : null);
  return {
    customer_id: apiSurvey.customer_id,
    roof_feasible: apiSurvey.roof_feasible ?? null,
    roof_rejection_reason: apiSurvey.roof_rejection_reason ?? null,
    roof_inspection_photos: existingUrls(apiSurvey.roof_inspection_photos),
    house_front_view_photos: existingUrls(apiSurvey.house_front_view_photos),
    meter_photos: existingUrls(apiSurvey.meter_photos),
    line_diagram_photos: existingUrls(apiSurvey.line_diagram_photos),
    obstacle_photos: existingUrls(apiSurvey.obstacle_photos),
    aadhar_photo: existingUrl(apiSurvey.aadhar_photo),
    bank_passbook_photo: existingUrl(apiSurvey.bank_passbook_photo),
    electricity_bill_photo: existingUrl(apiSurvey.electricity_bill_photo),
    qr_code_photo: existingUrl(apiSurvey.qr_code_photo),
    proposed_capacity_kw: apiSurvey.proposed_capacity_kw ?? null,
    sanctioned_load_kw: apiSurvey.sanctioned_load_kw ?? null,
    phase_connection: apiSurvey.phase_connection ?? null,
    lineman_name: apiSurvey.lineman_name ?? null,
    roof_type: apiSurvey.roof_type ?? null,
    total_roof_area_sqft: apiSurvey.total_roof_area_sqft ?? null,
    shadow_free_area_sqft: apiSurvey.shadow_free_area_sqft ?? null,
    roof_condition: apiSurvey.roof_condition ?? null,
    roof_orientation: apiSurvey.roof_orientation ?? null,
    parapet_height_ft: apiSurvey.parapet_height_ft ?? null,
    number_of_floors: apiSurvey.number_of_floors ?? null,
    access_type: apiSurvey.access_type ?? null,
    panel_placement: apiSurvey.panel_placement ?? null,
    meter_location: apiSurvey.meter_location ?? null,
    internet_status: apiSurvey.internet_status ?? null,
    earthing_soil_type: apiSurvey.earthing_soil_type ?? null,
    water_tank_present: apiSurvey.water_tank_present ?? false,
    neighbors_wall_present: apiSurvey.neighbors_wall_present ?? false,
    trees_present: apiSurvey.trees_present ?? false,
    no_obstacle: apiSurvey.no_obstacle ?? false,
    surveyor_signed: apiSurvey.surveyor_signed ?? false,
    customer_signed: apiSurvey.customer_signed ?? false,
    consent_accepted: apiSurvey.consent_accepted ?? false,
    survey_notes: apiSurvey.survey_notes ?? null,
    tentative_panel_location_photo: existingUrl(
      apiSurvey.tentative_panel_location_photo,
    ),
    tentative_inverter_location_photo: existingUrl(
      apiSurvey.tentative_inverter_location_photo,
    ),
    surveyor_signature_photo: existingUrl(apiSurvey.surveyor_signature_photo),
    customer_signature_photo: existingUrl(apiSurvey.customer_signature_photo),
    created_by: apiSurvey.created_by ?? null,
  };
}

const STATUS_ORDER = {
  survey_not_started: 1,
  survey_inprogress: 2,
  survey_complete: 3,
  survey_unworkable: 3,
  transport_pending: 4,
  transport_inprogress: 5,
  transport_complete: 6,
  civil_pending: 7,
  civil_inprogress: 8,
  civil_complete: 9,
  technical_pending: 10,
  technical_inprogress: 11,
  technical_complete: 12,
  commissioning_not_started: 13,
  commissioning_inprogress: 14,
  commissioning_complete: 15,
};

const CustomerSurvey = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [isGeoRuleEnforced, setIsGeoRuleEnforced] = useState(true);
  const [customerId, setCustomerId] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    aadhaar: "",
    phone: "",
    dob: "",
    serviceNumber: "",
    usn: "",
    address: "",
    village: "",
    district: "",
    state: "",
    pincode: "",
    region_id: "",
  });

  const [validationStatus, setValidationStatus] = useState("pending");
  const [matchedData, setMatchedData] = useState(null);
  const [newCustomerRegions, setNewCustomerRegions] = useState([]);

  // Roof inspection fields
  const [roofStatus, setRoofStatus] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  // Categorized photo arrays
  const [roofInspectionPhotos, setRoofInspectionPhotos] = useState([]);
  const [roofPhotos, setRoofPhotos] = useState([]);
  const [meterPhotos, setMeterPhotos] = useState([]);
  const [lineDiagramPhotos, setLineDiagramPhotos] = useState([]);
  const [obstaclePhotos, setObstaclePhotos] = useState([]);

  // Document photos
  const [aadharPhoto, setAadharPhoto] = useState(null);
  const [bankPassbookPhoto, setBankPassbookPhoto] = useState(null);
  const [electricityBillPhoto, setElectricityBillPhoto] = useState(null);

  // Tentative location photos
  const [tentativePanelLocationPhoto, setTentativePanelLocationPhoto] =
    useState(null);
  const [tentativeInverterLocationPhoto, setTentativeInverterLocationPhoto] =
    useState(null);

  // Signature photos (canvas export)
  const [surveyorSignaturePhoto, setSurveyorSignaturePhoto] = useState(null);
  const [customerSignaturePhoto, setCustomerSignaturePhoto] = useState(null);

  const [qrCode, setQrCode] = useState("");
  const [location, setLocation] = useState({
    verified: false,
    coordinates: null,
  });

  // Additional survey fields
  const [surveyData, setSurveyData] = useState({
    proposedCapacityKw: "",
    sanctionedLoadKw: "",
    phaseConnection: "",
    linemanName: "",
    roofType: "",
    totalRoofAreaSqft: "",
    shadowFreeAreaSqft: "",
    roofCondition: "",
    roofOrientation: "",
    parapetHeightFt: "",
    numberOfFloors: "",
    accessType: "",
    panelPlacement: "",
    meterLocation: "",
    internetStatus: "",
    earthingSoilType: "",
    waterTankPresent: false,
    neighborsWallPresent: false,
    treesPresent: false,
    noObstacle: false,
    surveyorSigned: false,
    customerSigned: false,
    consentAccepted: false,
    surveyNotes: "",
  });

  const [errors, setErrors] = useState({});

  const [uploadProgress, setUploadProgress] = useState(null); // { currentStep, totalSteps, completedFiles, totalFiles, label } during submit/save

  const overlayMeta = {
    customerName: formData?.name || "",
    village: formData?.village || "",
    region: matchedData?.region || matchedData?.cluster || "",
  };

  // When user captures location at site, use it for photo overlays so uploads from a different place (e.g. good internet) still show site coords
  const capturedLocation =
    location?.verified && location?.coordinates
      ? {
          latitude: location.coordinates.latitude,
          longitude: location.coordinates.longitude,
        }
      : null;

  // Count files to upload for finer progress (photos, documents, assets, QR)
  const getUploadFileCounts = useCallback(() => {
    const countFiles = (arr) =>
      (Array.isArray(arr) ? arr : []).filter((p) => p instanceof File).length;
    const phase1 =
      countFiles(roofInspectionPhotos) +
      countFiles(roofPhotos) +
      countFiles(meterPhotos) +
      countFiles(lineDiagramPhotos) +
      countFiles(obstaclePhotos);
    const phase2 =
      (aadharPhoto instanceof File ? 1 : 0) +
      (bankPassbookPhoto instanceof File ? 1 : 0) +
      (electricityBillPhoto instanceof File ? 1 : 0);
    const phase3 =
      (tentativePanelLocationPhoto instanceof File ? 1 : 0) +
      (tentativeInverterLocationPhoto instanceof File ? 1 : 0) +
      (surveyorSignaturePhoto instanceof File ? 1 : 0) +
      (customerSignaturePhoto instanceof File ? 1 : 0) +
      (qrCode instanceof File ? 1 : 0);
    return {
      totalFiles: phase1 + phase2 + phase3,
      phase1,
      phase2,
      phase3,
    };
  }, [
    roofInspectionPhotos,
    roofPhotos,
    meterPhotos,
    lineDiagramPhotos,
    obstaclePhotos,
    aadharPhoto,
    bankPassbookPhoto,
    electricityBillPhoto,
    tentativePanelLocationPhoto,
    tentativeInverterLocationPhoto,
    surveyorSignaturePhoto,
    customerSignaturePhoto,
    qrCode,
  ]);

  // Load customer and survey data from URL parameter
  useEffect(() => {
    const loadCustomerAndSurvey = async () => {
      const urlCustomerId = searchParams.get("customerId");
      if (!urlCustomerId) return;

      setIsLoadingData(true);
      try {
        // Fetch customer data
        const customer = await customersAPI.getById(urlCustomerId);
        if (customer) {
          // Prefill customer form data
          setFormData({
            name: customer.name || "",
            aadhaar: customer.aadhaar_number || "",
            phone: customer.phone_number || "",
            dob: customer.dob || "",
            serviceNumber: customer.cin || "",
            usn: customer.usn || "",
            address: customer.address || "",
            village: customer.village || "",
            district: customer.district || "",
            state: customer.state || "",
            pincode: customer.pincode || "",
            region_id: customer.region_id || "",
          });

          setCustomerId(customer.id);
          setIsNewCustomer(false); // Customer exists, not a new one
          setValidationStatus("success");
          setMatchedData({
            name: customer.name,
            aadhaar: customer.aadhaar_number,
            phone: customer.phone_number,
            cluster: customer.clusters?.name || "",
            region: customer.regions?.name || "",
          });

          // Set location if available
          if (customer.latitude && customer.longitude) {
            setLocation({
              verified: true,
              coordinates: {
                latitude: customer.latitude,
                longitude: customer.longitude,
              },
            });
          }

          // Fetch existing survey data
          const existingSurvey = await surveysAPI.getByCustomerId(customer.id);
          if (existingSurvey) {
            currentSurveyIdRef.current = existingSurvey.id;
            lastSavedSurveyRef.current = normalizeSurveyFromApi(existingSurvey);
            // Prefill survey data
            if (
              existingSurvey.roof_feasible !== null &&
              existingSurvey.roof_feasible !== undefined
            ) {
              setRoofStatus(
                existingSurvey.roof_feasible ? "acceptable" : "not_acceptable",
              );
            }

            if (existingSurvey.roof_rejection_reason) {
              setRejectionReason(existingSurvey.roof_rejection_reason);
            }

            // Handle categorized photos
            if (existingSurvey.roof_inspection_photos?.length > 0) {
              setRoofInspectionPhotos(existingSurvey.roof_inspection_photos);
            }
            if (existingSurvey.house_front_view_photos?.length > 0) {
              setRoofPhotos(existingSurvey.house_front_view_photos || []);
            }
            if (existingSurvey.meter_photos?.length > 0) {
              setMeterPhotos(existingSurvey.meter_photos);
            }
            if (existingSurvey.line_diagram_photos?.length > 0) {
              setLineDiagramPhotos(existingSurvey.line_diagram_photos);
            }
            if (existingSurvey.obstacle_photos?.length > 0) {
              setObstaclePhotos(existingSurvey.obstacle_photos);
            }

            // Document photos
            if (existingSurvey.aadhar_photo)
              setAadharPhoto(existingSurvey.aadhar_photo);
            if (existingSurvey.bank_passbook_photo)
              setBankPassbookPhoto(existingSurvey.bank_passbook_photo);
            if (existingSurvey.electricity_bill_photo)
              setElectricityBillPhoto(existingSurvey.electricity_bill_photo);

            // Tentative location photos
            if (existingSurvey.tentative_panel_location_photo) {
              setTentativePanelLocationPhoto(
                existingSurvey.tentative_panel_location_photo,
              );
            }
            if (existingSurvey.tentative_inverter_location_photo) {
              setTentativeInverterLocationPhoto(
                existingSurvey.tentative_inverter_location_photo,
              );
            }

            // Signature photos
            if (existingSurvey.surveyor_signature_photo) {
              setSurveyorSignaturePhoto(
                existingSurvey.surveyor_signature_photo,
              );
            }
            if (existingSurvey.customer_signature_photo) {
              setCustomerSignaturePhoto(
                existingSurvey.customer_signature_photo,
              );
            }

            if (existingSurvey.qr_code_photo) {
              setQrCode(existingSurvey.qr_code_photo);
            }

            // Load additional survey fields
            setSurveyData({
              proposedCapacityKw:
                existingSurvey.proposed_capacity_kw?.toString() || "",
              sanctionedLoadKw:
                existingSurvey.sanctioned_load_kw?.toString() || "",
              phaseConnection: existingSurvey.phase_connection || "",
              linemanName: existingSurvey.lineman_name || "",
              roofType: existingSurvey.roof_type || "",
              totalRoofAreaSqft:
                existingSurvey.total_roof_area_sqft?.toString() || "",
              shadowFreeAreaSqft:
                existingSurvey.shadow_free_area_sqft?.toString() || "",
              roofCondition: existingSurvey.roof_condition || "",
              roofOrientation: existingSurvey.roof_orientation || "",
              parapetHeightFt:
                existingSurvey.parapet_height_ft?.toString() || "",
              numberOfFloors: existingSurvey.number_of_floors?.toString() || "",
              accessType: existingSurvey.access_type || "",
              panelPlacement: existingSurvey.panel_placement || "",
              meterLocation: existingSurvey.meter_location || "",
              internetStatus: existingSurvey.internet_status || "",
              earthingSoilType: existingSurvey.earthing_soil_type || "",
              waterTankPresent: existingSurvey.water_tank_present || false,
              neighborsWallPresent:
                existingSurvey.neighbors_wall_present || false,
              treesPresent: existingSurvey.trees_present || false,
              noObstacle: existingSurvey.no_obstacle || false,
              surveyorSigned: existingSurvey.surveyor_signed || false,
              customerSigned: existingSurvey.customer_signed || false,
              consentAccepted: existingSurvey.consent_accepted || false,
              surveyNotes: existingSurvey.survey_notes || "",
            });

            // Determine current step based on what's completed
            if (existingSurvey.qr_code_photo) {
              setCurrentStep(9); // QR code done, all steps complete
            } else if (
              existingSurvey.surveyor_signed ||
              existingSurvey.customer_signed
            ) {
              setCurrentStep(8); // Sign-offs done, move to QR step
            } else if (
              existingSurvey.meter_location ||
              existingSurvey.internet_status
            ) {
              setCurrentStep(7); // Electrical/logistics done
            } else if (existingSurvey.aadhar_photo) {
              setCurrentStep(6); // Document photos done
            } else if (
              existingSurvey.house_front_view_photos?.length > 0 ||
              existingSurvey.meter_photos?.length > 0 ||
              existingSurvey.line_diagram_photos?.length > 0 ||
              existingSurvey.obstacle_photos?.length > 0
            ) {
              setCurrentStep(5); // Site photos done
            } else if (
              existingSurvey.roof_type ||
              existingSurvey.total_roof_area_sqft
            ) {
              setCurrentStep(4); // Roof engineering done
            } else if (
              existingSurvey.roof_feasible !== null &&
              existingSurvey.roof_feasible !== undefined
            ) {
              setCurrentStep(3); // Roof inspection done
            } else if (
              existingSurvey.proposed_capacity_kw ||
              existingSurvey.sanctioned_load_kw
            ) {
              setCurrentStep(2); // Survey details done
            }
          } else {
            currentSurveyIdRef.current = null;
            lastSavedSurveyRef.current = null;
            // No survey exists, start from step 1
            setCurrentStep(1);
          }
          lastSavedCustomerRef.current = {
            name: customer.name || "",
            aadhaar_number: customer.aadhaar_number || "",
            phone_number: customer.phone_number || "",
            dob: customer.dob || null,
            address: customer.address || "",
            village: customer.village || "",
            district: customer.district || null,
            state: customer.state || "",
            pincode: customer.pincode || "",
          };
          lastSavedCustomerStatusRef.current = customer.status || null;
        }
      } catch (error) {
        console.error("Error loading customer/survey data:", error);
        setErrors({
          load: "Failed to load customer data. Please try again.",
        });
      } finally {
        setIsLoadingData(false);
      }
    };

    loadCustomerAndSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch regions for step 1 (user's project) – used for both new and existing customer
  useEffect(() => {
    if (!user) {
      setNewCustomerRegions([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      let projectId = user.project_id || null;
      if (!projectId && user.cluster_id) {
        try {
          const cluster = await clustersAPI.getById(user.cluster_id);
          if (cluster) projectId = cluster.project_id;
        } catch (e) {
          console.warn("Failed to get cluster for regions", e);
        }
      }
      if (!projectId || cancelled) return;
      try {
        const list = await regionsAPI.getByProjectId(projectId);
        if (!cancelled) setNewCustomerRegions(list || []);
      } catch (e) {
        console.warn("Failed to fetch regions", e);
        if (!cancelled) setNewCustomerRegions([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.project_id, user?.cluster_id]);

  // Fetch customer when CIN is entered (only if not a new customer)
  useEffect(() => {
    if (isNewCustomer) {
      // Reset validation status when new customer mode is enabled
      setValidationStatus("pending");
      setCustomerId(null);
      setMatchedData(null);
      setIsLoadingCustomer(false);
      return;
    }

    const fetchCustomer = async () => {
      const cin = formData?.serviceNumber?.trim();
      if (!cin || cin.length < 2) {
        setValidationStatus("pending");
        setCustomerId(null);
        setMatchedData(null);
        return;
      }

      setIsLoadingCustomer(true);
      try {
        const customer = await customersAPI.getByCIN(cin);
        if (customer) {
          setFormData((prev) => ({
            ...prev,
            name: customer.name || "",
            aadhaar: customer.aadhaar_number || prev.aadhaar || "",
            phone: customer.phone_number || "",
            dob: customer.dob || "",
            usn: customer.usn || "",
            address: customer.address || "",
            village: customer.village || "",
            district: customer.district || "",
            state: customer.state || "",
            pincode: customer.pincode || "",
            region_id: customer.region_id || prev.region_id || "",
          }));

          setCustomerId(customer.id);
          setValidationStatus("success");
          setMatchedData({
            name: customer.name,
            cluster: customer.clusters?.name || "",
            region: customer.regions?.name || "",
          });

          lastSavedCustomerRef.current = {
            name: customer.name || "",
            aadhaar_number: customer.aadhaar_number || "",
            phone_number: customer.phone_number || "",
            dob: customer.dob || null,
            address: customer.address || "",
            village: customer.village || "",
            district: customer.district || null,
            state: customer.state || "",
            pincode: customer.pincode || "",
          };
          lastSavedCustomerStatusRef.current = customer.status || null;

          // Update customer status to survey_inprogress when survey is started
          if (customer.status === "survey_not_started" || !customer.status) {
            try {
              await customersAPI.update(customer.id, {
                status: "survey_inprogress",
              });
              lastSavedCustomerStatusRef.current = "survey_inprogress";
            } catch (error) {
              console.error(
                "Error updating customer status to survey_inprogress:",
                error,
              );
            }
          }
        } else {
          // Customer not found - show option to create new
          setValidationStatus("not_found");
          setCustomerId(null);
          setMatchedData(null);
        }
      } catch (error) {
        console.error("Error fetching customer:", error);
        setValidationStatus("error");
        setCustomerId(null);
        setMatchedData(null);
      } finally {
        setIsLoadingCustomer(false);
      }
    };

    // Debounce the fetch
    const timeoutId = setTimeout(() => {
      fetchCustomer();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData?.serviceNumber, isNewCustomer]);

  const saveProgressRef = useRef(null);
  const scheduleSaveRef = useRef(null);
  const currentSurveyIdRef = useRef(null);
  const lastSavedSurveyRef = useRef(null);
  const lastSavedCustomerRef = useRef(null);
  const lastSavedCustomerStatusRef = useRef(null);

  // Build full survey payload (API shape) from current form state; uses existing URLs only (no File).
  const getCurrentFullSurveyState = useCallback(() => {
    const existingUrl = (v) => (v && typeof v === "string" ? v : null);
    const existingUrls = (arr) =>
      (Array.isArray(arr) ? arr : []).filter((p) => typeof p === "string");
    return {
      customer_id: customerId,
      roof_feasible:
        roofStatus === "acceptable"
          ? true
          : roofStatus === "not_acceptable"
            ? false
            : null,
      roof_rejection_reason:
        roofStatus === "not_acceptable" ? rejectionReason : null,
      roof_inspection_photos: existingUrls(roofInspectionPhotos),
      house_front_view_photos: existingUrls(roofPhotos),
      meter_photos: existingUrls(meterPhotos),
      line_diagram_photos: existingUrls(lineDiagramPhotos),
      obstacle_photos: existingUrls(obstaclePhotos),
      aadhar_photo: existingUrl(aadharPhoto),
      bank_passbook_photo: existingUrl(bankPassbookPhoto),
      electricity_bill_photo: existingUrl(electricityBillPhoto),
      qr_code_photo: existingUrl(qrCode),
      proposed_capacity_kw: surveyData.proposedCapacityKw
        ? parseFloat(surveyData.proposedCapacityKw)
        : null,
      sanctioned_load_kw: surveyData.sanctionedLoadKw
        ? parseFloat(surveyData.sanctionedLoadKw)
        : null,
      phase_connection: surveyData.phaseConnection || null,
      lineman_name: surveyData.linemanName || null,
      roof_type: surveyData.roofType || null,
      total_roof_area_sqft: surveyData.totalRoofAreaSqft
        ? parseFloat(surveyData.totalRoofAreaSqft)
        : null,
      shadow_free_area_sqft: surveyData.shadowFreeAreaSqft
        ? parseFloat(surveyData.shadowFreeAreaSqft)
        : null,
      roof_condition: surveyData.roofCondition || null,
      roof_orientation: surveyData.roofOrientation || null,
      parapet_height_ft: surveyData.parapetHeightFt
        ? parseFloat(surveyData.parapetHeightFt)
        : null,
      number_of_floors: surveyData.numberOfFloors
        ? parseInt(surveyData.numberOfFloors)
        : null,
      access_type: surveyData.accessType || null,
      panel_placement: surveyData.panelPlacement || null,
      meter_location: surveyData.meterLocation || null,
      internet_status: surveyData.internetStatus || null,
      earthing_soil_type: surveyData.earthingSoilType || null,
      water_tank_present: surveyData.waterTankPresent || false,
      neighbors_wall_present: surveyData.neighborsWallPresent || false,
      trees_present: surveyData.treesPresent || false,
      no_obstacle: surveyData.noObstacle || false,
      surveyor_signed: surveyData.surveyorSigned || false,
      customer_signed: surveyData.customerSigned || false,
      consent_accepted: surveyData.consentAccepted || false,
      survey_notes: surveyData.surveyNotes || null,
      tentative_panel_location_photo: existingUrl(tentativePanelLocationPhoto),
      tentative_inverter_location_photo: existingUrl(
        tentativeInverterLocationPhoto,
      ),
      surveyor_signature_photo: existingUrl(surveyorSignaturePhoto),
      customer_signature_photo: existingUrl(customerSignaturePhoto),
      created_by: user?.id || null,
    };
  }, [
    customerId,
    roofStatus,
    rejectionReason,
    roofInspectionPhotos,
    roofPhotos,
    meterPhotos,
    lineDiagramPhotos,
    obstaclePhotos,
    aadharPhoto,
    bankPassbookPhoto,
    electricityBillPhoto,
    qrCode,
    surveyData,
    tentativePanelLocationPhoto,
    tentativeInverterLocationPhoto,
    surveyorSignaturePhoto,
    customerSignaturePhoto,
    user?.id,
  ]);

  const diffObjects = (oldObj, newObj) => {
    if (!oldObj || !newObj) return newObj ? { ...newObj } : {};
    const diff = {};
    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
    for (const key of allKeys) {
      const oldVal = oldObj[key];
      const newVal = newObj[key];
      const same =
        oldVal === newVal ||
        (typeof oldVal === "object" &&
          typeof newVal === "object" &&
          JSON.stringify(oldVal) === JSON.stringify(newVal));
      if (!same) diff[key] = newVal;
    }
    return diff;
  };

  const handleFormChange = (field, value) => {
    if (field === "aadhaar") {
      let cleaned = value?.toString().replace(/\D/g, "");
      if (cleaned?.length > 12) cleaned = cleaned?.slice(0, 12);
      setFormData((prev) => ({ ...prev, [field]: cleaned || "" }));
    } else if (field === "phone") {
      let cleaned = value?.replace(/\D/g, "")?.slice(0, 10);
      setFormData((prev) => ({ ...prev, [field]: cleaned }));
    } else if (field === "pincode") {
      let cleaned = value?.replace(/\D/g, "")?.slice(0, 6);
      setFormData((prev) => ({ ...prev, [field]: cleaned }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    scheduleSaveRef.current?.();
  };

  const handleSurveyDataChange = (field, value) => {
    setSurveyData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // If user selects "No Obstacle", obstacle photos should not be required.
    if (field === "noObstacle" && value === true && errors?.obstaclePhotos) {
      setErrors((prev) => ({ ...prev, obstaclePhotos: "" }));
    }
    scheduleSaveRef.current?.();
  };

  const validateStep = (step) => {
    void step;
    setErrors({});
    return true;
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      setIsLoadingNext(true);
      try {
        // Handle customer creation or update when moving from step 1 to step 2
        if (currentStep === 1) {
          try {
            if (isNewCustomer) {
              // Create new customer: project from user, region from form selection
              const regionId = formData.region_id || null;
              let projectId = user?.project_id || null;
              let clusterId = user?.cluster_id || null;

              if (user?.cluster_id) {
                try {
                  const cluster = await clustersAPI.getById(user.cluster_id);
                  if (cluster) {
                    clusterId = cluster.id;
                    projectId = projectId || cluster.project_id;
                  }
                } catch (error) {
                  console.error("Error fetching cluster:", error);
                }
              }

              if (!projectId) {
                setErrors({
                  submit:
                    "Unable to determine project. Please contact administrator.",
                });
                setIsLoadingNext(false);
                return;
              }
              const newCustomer = await customersAPI.create({
                name: formData.name,
                aadhaar_number: formData.aadhaar || null,
                phone_number: formData.phone,
                dob: formData.dob || null,
                address: formData.address,
                village: formData.village,
                district: formData.district || null,
                state: formData.state || "Andhra Pradesh",
                pincode: formData.pincode,
                project_id: projectId,
                region_id: regionId,
                cluster_id: clusterId,
                status: "survey_inprogress",
              });

              setCustomerId(newCustomer.id);
              setValidationStatus("success");
            } else if (customerId) {
              await customersAPI.update(customerId, {
                name: formData.name,
                aadhaar_number: formData.aadhaar,
                phone_number: formData.phone,
                dob: formData.dob || null,
                address: formData.address,
                village: formData.village,
                district: formData.district || null,
                state: formData.state,
                pincode: formData.pincode,
              });
            }
          } catch (error) {
            console.error("Error saving customer details:", error);
            setErrors({
              submit:
                error.message ||
                "Failed to save customer details. Please try again.",
            });
            setIsLoadingNext(false);
            return;
          }
        }

        if (currentStep < steps.length) {
          setCurrentStep((prev) => prev + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } finally {
        setIsLoadingNext(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1 && !isSaving) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleStepClick = (stepNumber) => {
    if (stepNumber !== currentStep && !isSaving) {
      setCurrentStep(stepNumber);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const saveProgress = async (options = {}) => {
    const { silent = false } = options;
    if (!customerId) {
      if (!silent)
        setErrors({
          save: "Customer not found. Please complete customer details first.",
        });
      return;
    }
    if (currentStep === 1 && isNewCustomer) {
      if (!silent)
        setErrors({ save: "Please complete customer creation first." });
      return;
    }

    setIsSaving(true);
    setUploadProgress(null);
    try {
      const surveyId = currentSurveyIdRef.current;
      const { totalFiles, phase1, phase2, phase3 } = getUploadFileCounts();
      const totalSteps = 4;
      const setProgress = (step, completedFiles, label) =>
        setUploadProgress({
          currentStep: step,
          totalSteps,
          completedFiles,
          totalFiles,
          label,
        });

      let savedSurvey = null;

      if (surveyId && totalFiles > 0) {
        // Existing survey + new files: upload first, then PATCH only changed fields (including new URLs).
        setProgress(1, 0, "Photos & documents");
        const uploadPhotoCategory = async (photos, categoryName) => {
          if (!photos?.length) return [];
          const filesToUpload = photos.filter((photo) => photo instanceof File);
          const existingUrls = photos.filter(
            (photo) => typeof photo === "string",
          );
          if (filesToUpload.length === 0) return existingUrls;
          return [
            ...existingUrls,
            ...(await surveysAPI.uploadCategoryPhotos(
              surveyId,
              filesToUpload,
              categoryName,
            )),
          ];
        };
        const [
          roofInspectionUrls,
          roofUrls,
          meterUrls,
          lineUrls,
          obstacleUrls,
        ] = await Promise.all([
          uploadPhotoCategory(roofInspectionPhotos, "roof_inspection"),
          uploadPhotoCategory(roofPhotos, "roof"),
          uploadPhotoCategory(meterPhotos, "meter"),
          uploadPhotoCategory(lineDiagramPhotos, "line_diagram"),
          uploadPhotoCategory(obstaclePhotos, "obstacle"),
        ]);

        setProgress(2, phase1, "Documents");
        const uploadDocumentPhoto = (photo, fieldName) =>
          photo && photo instanceof File
            ? surveysAPI.uploadDocumentPhoto(surveyId, photo, fieldName)
            : Promise.resolve(null);
        const [aadharUrl, bankUrl, billUrl] = await Promise.all([
          uploadDocumentPhoto(aadharPhoto, "aadhar"),
          uploadDocumentPhoto(bankPassbookPhoto, "bank_passbook"),
          uploadDocumentPhoto(electricityBillPhoto, "electricity_bill"),
        ]);
        if (aadharUrl) setAadharPhoto(aadharUrl);
        if (bankUrl) setBankPassbookPhoto(bankUrl);
        if (billUrl) setElectricityBillPhoto(billUrl);

        setProgress(3, phase1 + phase2, "Signatures & QR");
        const uploadAsset = (fileOrUrl, fieldName) =>
          fileOrUrl && fileOrUrl instanceof File
            ? surveysAPI.uploadSurveyAsset(surveyId, fileOrUrl, fieldName, {
                skipSurveyUpdate: true,
              })
            : Promise.resolve(null);
        const [
          panelLocUrl,
          inverterLocUrl,
          surveyorSigUrl,
          customerSigUrl,
          qrCodeUrl,
        ] = await Promise.all([
          uploadAsset(
            tentativePanelLocationPhoto,
            "tentative_panel_location_photo",
          ),
          uploadAsset(
            tentativeInverterLocationPhoto,
            "tentative_inverter_location_photo",
          ),
          uploadAsset(surveyorSignaturePhoto, "surveyor_signature_photo"),
          uploadAsset(customerSignaturePhoto, "customer_signature_photo"),
          qrCode && qrCode instanceof File
            ? surveysAPI.uploadQRCode(surveyId, qrCode, {
                skipSurveyUpdate: true,
              })
            : Promise.resolve(null),
        ]);
        if (panelLocUrl) setTentativePanelLocationPhoto(panelLocUrl);
        if (inverterLocUrl) setTentativeInverterLocationPhoto(inverterLocUrl);
        if (surveyorSigUrl) setSurveyorSignaturePhoto(surveyorSigUrl);
        if (customerSigUrl) setCustomerSignaturePhoto(customerSigUrl);
        if (qrCodeUrl) setQrCode(qrCodeUrl);

        const photoUpdates = {};
        if (roofInspectionUrls.length > 0) {
          photoUpdates.roof_inspection_photos = roofInspectionUrls;
          setRoofInspectionPhotos(roofInspectionUrls);
        }
        if (roofUrls.length > 0) {
          photoUpdates.house_front_view_photos = roofUrls;
          setRoofPhotos(roofUrls);
        }
        if (meterUrls.length > 0) {
          photoUpdates.meter_photos = meterUrls;
          setMeterPhotos(meterUrls);
        }
        if (lineUrls.length > 0) {
          photoUpdates.line_diagram_photos = lineUrls;
          setLineDiagramPhotos(lineUrls);
        }
        if (obstacleUrls.length > 0) {
          photoUpdates.obstacle_photos = obstacleUrls;
          setObstaclePhotos(obstacleUrls);
        }
        if (aadharUrl) photoUpdates.aadhar_photo = aadharUrl;
        if (bankUrl) photoUpdates.bank_passbook_photo = bankUrl;
        if (billUrl) photoUpdates.electricity_bill_photo = billUrl;
        if (panelLocUrl)
          photoUpdates.tentative_panel_location_photo = panelLocUrl;
        if (inverterLocUrl)
          photoUpdates.tentative_inverter_location_photo = inverterLocUrl;
        if (surveyorSigUrl)
          photoUpdates.surveyor_signature_photo = surveyorSigUrl;
        if (customerSigUrl)
          photoUpdates.customer_signature_photo = customerSigUrl;
        if (qrCodeUrl) photoUpdates.qr_code_photo = qrCodeUrl;

        const currentState = {
          ...getCurrentFullSurveyState(),
          ...photoUpdates,
        };
        const surveyDiff = diffObjects(
          lastSavedSurveyRef.current,
          currentState,
        );
        const {
          customer_id: _c,
          created_by: _b,
          ...updatePayload
        } = surveyDiff;
        if (Object.keys(updatePayload).length > 0) {
          setProgress(4, totalFiles, "Saving");
          await surveysAPI.update(surveyId, updatePayload);
        }
        lastSavedSurveyRef.current = currentState;
      } else if (surveyId && totalFiles === 0) {
        // Existing survey, no new files: PATCH only changed fields.
        const currentState = getCurrentFullSurveyState();
        const surveyDiff = diffObjects(
          lastSavedSurveyRef.current,
          currentState,
        );
        const {
          customer_id: _c,
          created_by: _b,
          ...updatePayload
        } = surveyDiff;
        if (Object.keys(updatePayload).length > 0) {
          await surveysAPI.update(surveyId, updatePayload);
        }
        lastSavedSurveyRef.current = currentState;
      } else {
        // No existing survey: create with full payload, then upload if any.
        const surveyPayload = getCurrentFullSurveyState();
        savedSurvey = await surveysAPI.upsert(surveyPayload);
        currentSurveyIdRef.current = savedSurvey.id;
        lastSavedSurveyRef.current = { ...surveyPayload };

        if (savedSurvey && totalFiles > 0) {
          setProgress(1, 0, "Photos & documents");
          const uploadPhotoCategory = async (photos, categoryName) => {
            if (!photos?.length) return [];
            const filesToUpload = photos.filter(
              (photo) => photo instanceof File,
            );
            const existingUrls = photos.filter(
              (photo) => typeof photo === "string",
            );
            if (filesToUpload.length === 0) return existingUrls;
            return [
              ...existingUrls,
              ...(await surveysAPI.uploadCategoryPhotos(
                savedSurvey.id,
                filesToUpload,
                categoryName,
              )),
            ];
          };
          const [
            roofInspectionUrls,
            roofUrls,
            meterUrls,
            lineUrls,
            obstacleUrls,
          ] = await Promise.all([
            uploadPhotoCategory(roofInspectionPhotos, "roof_inspection"),
            uploadPhotoCategory(roofPhotos, "roof"),
            uploadPhotoCategory(meterPhotos, "meter"),
            uploadPhotoCategory(lineDiagramPhotos, "line_diagram"),
            uploadPhotoCategory(obstaclePhotos, "obstacle"),
          ]);

          setProgress(2, phase1, "Documents");
          const uploadDocumentPhoto = (photo, fieldName) =>
            photo && photo instanceof File
              ? surveysAPI.uploadDocumentPhoto(savedSurvey.id, photo, fieldName)
              : Promise.resolve(null);
          const [aadharUrl, bankUrl, billUrl] = await Promise.all([
            uploadDocumentPhoto(aadharPhoto, "aadhar"),
            uploadDocumentPhoto(bankPassbookPhoto, "bank_passbook"),
            uploadDocumentPhoto(electricityBillPhoto, "electricity_bill"),
          ]);
          if (aadharUrl) setAadharPhoto(aadharUrl);
          if (bankUrl) setBankPassbookPhoto(bankUrl);
          if (billUrl) setElectricityBillPhoto(billUrl);

          setProgress(3, phase1 + phase2, "Signatures & QR");
          const uploadAsset = (fileOrUrl, fieldName) =>
            fileOrUrl && fileOrUrl instanceof File
              ? surveysAPI.uploadSurveyAsset(
                  savedSurvey.id,
                  fileOrUrl,
                  fieldName,
                  { skipSurveyUpdate: true },
                )
              : Promise.resolve(null);
          const [
            panelLocUrl,
            inverterLocUrl,
            surveyorSigUrl,
            customerSigUrl,
            qrCodeUrl,
          ] = await Promise.all([
            uploadAsset(
              tentativePanelLocationPhoto,
              "tentative_panel_location_photo",
            ),
            uploadAsset(
              tentativeInverterLocationPhoto,
              "tentative_inverter_location_photo",
            ),
            uploadAsset(surveyorSignaturePhoto, "surveyor_signature_photo"),
            uploadAsset(customerSignaturePhoto, "customer_signature_photo"),
            qrCode && qrCode instanceof File
              ? surveysAPI.uploadQRCode(savedSurvey.id, qrCode, {
                  skipSurveyUpdate: true,
                })
              : Promise.resolve(null),
          ]);
          if (panelLocUrl) setTentativePanelLocationPhoto(panelLocUrl);
          if (inverterLocUrl) setTentativeInverterLocationPhoto(inverterLocUrl);
          if (surveyorSigUrl) setSurveyorSignaturePhoto(surveyorSigUrl);
          if (customerSigUrl) setCustomerSignaturePhoto(customerSigUrl);
          if (qrCodeUrl) setQrCode(qrCodeUrl);

          const allUpdates = {};
          if (roofInspectionUrls.length > 0) {
            allUpdates.roof_inspection_photos = roofInspectionUrls;
            setRoofInspectionPhotos(roofInspectionUrls);
          }
          if (roofUrls.length > 0) {
            allUpdates.house_front_view_photos = roofUrls;
            setRoofPhotos(roofUrls);
          }
          if (meterUrls.length > 0) {
            allUpdates.meter_photos = meterUrls;
            setMeterPhotos(meterUrls);
          }
          if (lineUrls.length > 0) {
            allUpdates.line_diagram_photos = lineUrls;
            setLineDiagramPhotos(lineUrls);
          }
          if (obstacleUrls.length > 0) {
            allUpdates.obstacle_photos = obstacleUrls;
            setObstaclePhotos(obstacleUrls);
          }
          if (aadharUrl) allUpdates.aadhar_photo = aadharUrl;
          if (bankUrl) allUpdates.bank_passbook_photo = bankUrl;
          if (billUrl) allUpdates.electricity_bill_photo = billUrl;
          if (panelLocUrl)
            allUpdates.tentative_panel_location_photo = panelLocUrl;
          if (inverterLocUrl)
            allUpdates.tentative_inverter_location_photo = inverterLocUrl;
          if (surveyorSigUrl)
            allUpdates.surveyor_signature_photo = surveyorSigUrl;
          if (customerSigUrl)
            allUpdates.customer_signature_photo = customerSigUrl;
          if (qrCodeUrl) allUpdates.qr_code_photo = qrCodeUrl;
          if (Object.keys(allUpdates).length > 0) {
            setProgress(4, totalFiles, "Saving");
            await surveysAPI.update(savedSurvey.id, allUpdates);
          }
          lastSavedSurveyRef.current = {
            ...lastSavedSurveyRef.current,
            ...allUpdates,
          };
        }
      }

      // Customer: PATCH only when step 1 and customer fields changed.
      if (currentStep === 1 && customerId) {
        const customerPayload = {
          name: formData.name,
          aadhaar_number: formData.aadhaar,
          phone_number: formData.phone,
          dob: formData.dob || null,
          address: formData.address,
          village: formData.village,
          district: formData.district || null,
          state: formData.state,
          pincode: formData.pincode,
        };
        const customerDiff = diffObjects(
          lastSavedCustomerRef.current,
          customerPayload,
        );
        if (Object.keys(customerDiff).length > 0) {
          await customersAPI.update(customerId, customerDiff);
          lastSavedCustomerRef.current = customerPayload;
        }
      }

      if (
        roofStatus === "not_acceptable" &&
        customerId &&
        lastSavedCustomerStatusRef.current !== "survey_unworkable"
      ) {
        try {
          await customersAPI.update(customerId, {
            status: "survey_unworkable",
          });
          lastSavedCustomerStatusRef.current = "survey_unworkable";
        } catch (e) {
          console.error(
            "Error updating customer status to survey_unworkable on save:",
            e,
          );
        }
      }

      setErrors((prev) => (silent ? prev : {}));
      setLastSavedAt(new Date());
      if (!silent) showToast("Progress saved successfully!", "success");
    } catch (error) {
      console.error("Error saving survey progress:", error);
      const msg =
        error?.message || "Failed to save progress. Please try again.";
      setErrors((prev) => ({ ...prev, save: msg }));
      if (silent) showToast(msg, "error");
    } finally {
      setUploadProgress(null);
      setIsSaving(false);
    }
  };
  saveProgressRef.current = saveProgress;

  const saveDebounceMs = 1500;
  const saveTimeoutRef = useRef(null);
  const scheduleSave = useCallback(() => {
    if (!customerId || (currentStep === 1 && isNewCustomer)) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      saveProgressRef.current?.({ silent: true }).catch(() => {});
    }, saveDebounceMs);
  }, [customerId, currentStep, isNewCustomer]);
  scheduleSaveRef.current = scheduleSave;

  useEffect(() => {
    let mounted = true;
    const loadGeoRuleSetting = async () => {
      try {
        const enabled = await appSettingsAPI.getGeolocationEnforcement();
        if (mounted) setIsGeoRuleEnforced(enabled);
      } catch {
        if (mounted) setIsGeoRuleEnforced(true);
      }
    };
    loadGeoRuleSetting();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const handleCaptureLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocation({ verified: false, coordinates: null, error: true });
      return Promise.reject(new Error("Geolocation not supported"));
    }
    if (!customerId) {
      setErrors((prev) => ({
        ...prev,
        location: "Customer not loaded. Please complete earlier steps first.",
      }));
      return Promise.reject(new Error("No customer"));
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };
          try {
            await customersAPI.update(customerId, {
              latitude: coords.latitude,
              longitude: coords.longitude,
            });
            setLocation({ verified: true, coordinates: coords, error: false });
            setErrors((prev) => ({ ...prev, location: "" }));
            resolve(coords);
          } catch (err) {
            console.error("Failed to save location to customer:", err);
            setLocation({ verified: true, coordinates: coords, error: false });
            setErrors((prev) => ({
              ...prev,
              location:
                "Location captured but failed to save. Please try again.",
            }));
            reject(err);
          }
        },
        (error) => {
          console.error("Location error:", error);
          setLocation({ verified: false, coordinates: null, error: true });
          setErrors((prev) => ({
            ...prev,
            location:
              "Location access denied. Please enable location and try again.",
          }));
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });
  }, [customerId]);

  const handleSubmit = async () => {
    // Validate all steps before final submit
    for (let step = 1; step <= steps.length; step += 1) {
      const ok = validateStep(step);
      if (!ok) {
        setCurrentStep(step);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }

    if (!customerId) {
      setErrors({
        submit: "Customer not found. Please check the CIN.",
      });
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(null);

    try {
      const locationCoordinates = location?.verified
        ? location.coordinates
        : null;
      if (isGeoRuleEnforced && !locationCoordinates) {
        setErrors((prev) => ({
          ...prev,
          location: "Please capture location before submitting.",
        }));
        setIsSubmitting(false);
        return;
      }

      // Create or update survey record (upsert handles existing surveys)
      const surveyPayload = {
        customer_id: customerId,
        approval_status: "pending",
        approval_decision_by: null,
        approval_decision_at: null,
        disapproval_reason: null,
        submitted_at: new Date().toISOString(),
        roof_feasible: roofStatus === "acceptable",
        roof_rejection_reason:
          roofStatus === "not_acceptable" ? rejectionReason : null,
        roof_inspection_photos: [], // Will be updated after upload
        house_front_view_photos: [], // Will be updated after upload
        meter_photos: [], // Will be updated after upload
        line_diagram_photos: [], // Will be updated after upload
        obstacle_photos: [], // Will be updated after upload
        aadhar_photo:
          aadharPhoto && typeof aadharPhoto === "string" ? aadharPhoto : null,
        bank_passbook_photo:
          bankPassbookPhoto && typeof bankPassbookPhoto === "string"
            ? bankPassbookPhoto
            : null,
        electricity_bill_photo:
          electricityBillPhoto && typeof electricityBillPhoto === "string"
            ? electricityBillPhoto
            : null,
        qr_code_photo: qrCode && typeof qrCode === "string" ? qrCode : null,
        proposed_capacity_kw: surveyData.proposedCapacityKw
          ? parseFloat(surveyData.proposedCapacityKw)
          : null,
        sanctioned_load_kw: surveyData.sanctionedLoadKw
          ? parseFloat(surveyData.sanctionedLoadKw)
          : null,
        phase_connection: surveyData.phaseConnection || null,
        lineman_name: surveyData.linemanName || null,
        roof_type: surveyData.roofType || null,
        total_roof_area_sqft: surveyData.totalRoofAreaSqft
          ? parseFloat(surveyData.totalRoofAreaSqft)
          : null,
        shadow_free_area_sqft: surveyData.shadowFreeAreaSqft
          ? parseFloat(surveyData.shadowFreeAreaSqft)
          : null,
        roof_condition: surveyData.roofCondition || null,
        roof_orientation: surveyData.roofOrientation || null,
        parapet_height_ft: surveyData.parapetHeightFt
          ? parseFloat(surveyData.parapetHeightFt)
          : null,
        number_of_floors: surveyData.numberOfFloors
          ? parseInt(surveyData.numberOfFloors)
          : null,
        access_type: surveyData.accessType || null,
        panel_placement: surveyData.panelPlacement || null,
        meter_location: surveyData.meterLocation || null,
        internet_status: surveyData.internetStatus || null,
        earthing_soil_type: surveyData.earthingSoilType || null,
        water_tank_present: surveyData.waterTankPresent || false,
        neighbors_wall_present: surveyData.neighborsWallPresent || false,
        trees_present: surveyData.treesPresent || false,
        no_obstacle: surveyData.noObstacle || false,
        surveyor_signed: surveyData.surveyorSigned || false,
        customer_signed: surveyData.customerSigned || false,
        consent_accepted: surveyData.consentAccepted || false,
        survey_notes: surveyData.surveyNotes || null,
        tentative_panel_location_photo:
          tentativePanelLocationPhoto &&
          typeof tentativePanelLocationPhoto === "string"
            ? tentativePanelLocationPhoto
            : null,
        tentative_inverter_location_photo:
          tentativeInverterLocationPhoto &&
          typeof tentativeInverterLocationPhoto === "string"
            ? tentativeInverterLocationPhoto
            : null,
        surveyor_signature_photo:
          surveyorSignaturePhoto && typeof surveyorSignaturePhoto === "string"
            ? surveyorSignaturePhoto
            : null,
        customer_signature_photo:
          customerSignaturePhoto && typeof customerSignaturePhoto === "string"
            ? customerSignaturePhoto
            : null,
        created_by: user?.id || null,
      };

      // Use upsert to handle existing surveys (unique constraint on customer_id)
      const createdSurvey = await surveysAPI.upsert(surveyPayload, {
        userId: user?.id,
      });

      // Sequential uploads with progress
      const uploadPhotoCategory = async (photos, categoryName) => {
        if (!photos?.length) return [];
        const filesToUpload = photos.filter((photo) => photo instanceof File);
        const existingUrls = photos.filter(
          (photo) => typeof photo === "string",
        );
        if (filesToUpload.length === 0) return existingUrls;
        return [
          ...existingUrls,
          ...(await surveysAPI.uploadCategoryPhotos(
            createdSurvey.id,
            filesToUpload,
            categoryName,
          )),
        ];
      };

      const { totalFiles, phase1, phase2, phase3 } = getUploadFileCounts();
      const totalSteps = 4;
      const setProgress = (currentStep, completedFiles, label) =>
        setUploadProgress({
          currentStep,
          totalSteps,
          completedFiles,
          totalFiles,
          label,
        });

      setProgress(1, 0, "Photos & documents");
      const [roofInspectionUrls, roofUrls, meterUrls, lineUrls, obstacleUrls] =
        await Promise.all([
          uploadPhotoCategory(roofInspectionPhotos, "roof_inspection"),
          uploadPhotoCategory(roofPhotos, "roof"),
          uploadPhotoCategory(meterPhotos, "meter"),
          uploadPhotoCategory(lineDiagramPhotos, "line_diagram"),
          uploadPhotoCategory(obstaclePhotos, "obstacle"),
        ]);

      setProgress(2, phase1, "Documents");
      const uploadDocumentPhoto = (photo, fieldName) =>
        photo && photo instanceof File
          ? surveysAPI.uploadDocumentPhoto(createdSurvey.id, photo, fieldName)
          : Promise.resolve(null);
      const [aadharUrl, bankUrl, billUrl] = await Promise.all([
        uploadDocumentPhoto(aadharPhoto, "aadhar"),
        uploadDocumentPhoto(bankPassbookPhoto, "bank_passbook"),
        uploadDocumentPhoto(electricityBillPhoto, "electricity_bill"),
      ]);

      setProgress(3, phase1 + phase2, "Signatures & QR");
      const uploadAsset = (fileOrUrl, fieldName) =>
        fileOrUrl && fileOrUrl instanceof File
          ? surveysAPI.uploadSurveyAsset(
              createdSurvey.id,
              fileOrUrl,
              fieldName,
              { skipSurveyUpdate: true },
            )
          : Promise.resolve(null);
      const [
        panelLocUrl,
        inverterLocUrl,
        surveyorSigUrl,
        customerSigUrl,
        qrCodeUrl,
      ] = await Promise.all([
        uploadAsset(
          tentativePanelLocationPhoto,
          "tentative_panel_location_photo",
        ),
        uploadAsset(
          tentativeInverterLocationPhoto,
          "tentative_inverter_location_photo",
        ),
        uploadAsset(surveyorSignaturePhoto, "surveyor_signature_photo"),
        uploadAsset(customerSignaturePhoto, "customer_signature_photo"),
        qrCode && qrCode instanceof File
          ? surveysAPI.uploadQRCode(createdSurvey.id, qrCode, {
              skipSurveyUpdate: true,
            })
          : Promise.resolve(null),
      ]);

      const photoUpdates = {};
      if (roofInspectionUrls.length > 0)
        photoUpdates.roof_inspection_photos = roofInspectionUrls;
      if (roofUrls.length > 0) photoUpdates.house_front_view_photos = roofUrls;
      if (meterUrls.length > 0) photoUpdates.meter_photos = meterUrls;
      if (lineUrls.length > 0) photoUpdates.line_diagram_photos = lineUrls;
      if (obstacleUrls.length > 0) photoUpdates.obstacle_photos = obstacleUrls;

      const allUpdates = {
        ...photoUpdates,
        ...(aadharUrl && { aadhar_photo: aadharUrl }),
        ...(bankUrl && { bank_passbook_photo: bankUrl }),
        ...(billUrl && { electricity_bill_photo: billUrl }),
        ...(panelLocUrl && {
          tentative_panel_location_photo: panelLocUrl,
        }),
        ...(inverterLocUrl && {
          tentative_inverter_location_photo: inverterLocUrl,
        }),
        ...(surveyorSigUrl && {
          surveyor_signature_photo: surveyorSigUrl,
        }),
        ...(customerSigUrl && {
          customer_signature_photo: customerSigUrl,
        }),
        ...(qrCodeUrl && { qr_code_photo: qrCodeUrl }),
      };
      if (Object.keys(allUpdates).length > 0) {
        setProgress(4, totalFiles, "Saving");
        await surveysAPI.update(createdSurvey.id, allUpdates, {
          userId: user?.id,
        });
      }

      // Update customer status and location
      const latestCustomer = await customersAPI.getById(customerId);
      const targetSurveyStatus =
        roofStatus === "acceptable" ? "survey_complete" : "survey_unworkable";
      const shouldMoveSurveyStatus =
        (STATUS_ORDER[targetSurveyStatus] || 0) >
        (STATUS_ORDER[latestCustomer?.status] || 0);
      const customerUpdates = shouldMoveSurveyStatus
        ? {
            status: targetSurveyStatus,
          }
        : {};

      // Store location coordinates if captured
      if (locationCoordinates) {
        customerUpdates.latitude = locationCoordinates.latitude;
        customerUpdates.longitude = locationCoordinates.longitude;
      }

      await customersAPI.update(customerId, customerUpdates);

      // Navigate immediately; run assignment and notification in background
      navigate("/worker-dashboard");

      // Deferred: assignment and notification (non-blocking)
      const runDeferred = async () => {
        try {
          const existingAssignment =
            await staffAssignmentsAPI.getByCustomerAndUser(customerId, user.id);
          if (existingAssignment) {
            await staffAssignmentsAPI.markCompleted(customerId, user.id);
          } else {
            await staffAssignmentsAPI.create(customerId, user.id);
            await staffAssignmentsAPI.markCompleted(customerId, user.id);
          }
        } catch (e) {
          console.error("Error updating assignment status:", e);
        }
        try {
          await notificationsAPI.notifyClusterManagerOnWorkCompletion(
            customerId,
            "survey",
          );
        } catch (e) {
          console.error("Error creating cluster manager notification:", e);
        }
      };
      runDeferred();
    } catch (error) {
      console.error("Survey submission error:", error);
      setErrors({
        submit: error?.message || "Failed to submit survey. Please try again.",
      });
    } finally {
      setUploadProgress(null);
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: "Customer Details", icon: "User" },
    { number: 2, title: "Survey Details", icon: "FileText" },
    { number: 3, title: "Roof Inspection", icon: "Home" },
    { number: 4, title: "Roof Engineering", icon: "Home" },
    { number: 5, title: "Site Photos", icon: "Camera" },
    { number: 6, title: "Document Photos", icon: "FileText" },
    { number: 7, title: "Electrical & Logistics", icon: "Zap" },
    { number: 8, title: "Obstacles & Sign-offs", icon: "AlertTriangle" },
    { number: 9, title: "QR & Location", icon: "MapPin" },
  ];

  // Show loading state while fetching customer/survey data
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading survey data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="content-area">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="ClipboardList" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                  Customer Survey
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Complete site assessment and customer verification
                </p>
              </div>
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate(-1)}
                className="ml-auto"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              {/* Horizontal scroll on mobile/tablet/laptop; spread layout only on xl+ */}
              <div className="overflow-x-auto -mx-1 px-1 pb-1 xl:overflow-visible xl:mx-0 xl:px-0 xl:pb-0">
                <div
                  className={`flex items-center flex-nowrap min-w-0 xl:flex-wrap xl:justify-between mb-4 ${isSaving ? "pointer-events-none opacity-60" : ""}`}
                >
                  {steps?.map((step, index) => (
                    <React.Fragment key={step?.number}>
                      <div
                        className="flex flex-col items-center gap-2 flex-shrink-0 min-w-[72px] xl:min-w-0 xl:flex-1 cursor-pointer"
                        onClick={() => handleStepClick(step.number)}
                      >
                        <div
                          className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                            currentStep === step?.number
                              ? "border-primary bg-primary text-primary-foreground"
                              : currentStep > step?.number
                                ? "border-success bg-success text-success-foreground hover:bg-success/80"
                                : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:bg-primary/5"
                          }`}
                        >
                          {currentStep > step?.number ? (
                            <Icon name="Check" size={20} />
                          ) : (
                            <Icon name={step?.icon} size={20} />
                          )}
                        </div>
                        <span
                          className={`text-xs md:text-sm font-medium text-center transition-colors whitespace-nowrap xl:whitespace-normal xl:break-words ${
                            currentStep === step?.number
                              ? "text-primary"
                              : currentStep > step?.number
                                ? "text-success"
                                : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {step?.title}
                        </span>
                      </div>
                      {index < steps?.length - 1 && (
                        <div
                          className={`flex-shrink-0 w-3 xl:flex-1 xl:w-auto h-0.5 mx-1 xl:mx-2 transition-all ${
                            currentStep > step?.number
                              ? "bg-success"
                              : "bg-border"
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            {currentStep === 1 && (
              <>
                <CustomerDetailsForm
                  formData={formData}
                  errors={errors}
                  onChange={handleFormChange}
                  isLoadingCustomer={isLoadingCustomer}
                  isNewCustomer={isNewCustomer}
                  onNewCustomerChange={(checked) => {
                    setIsNewCustomer(checked);
                    if (!checked)
                      setFormData((prev) => ({ ...prev, region_id: "" }));
                  }}
                  validationStatus={validationStatus}
                  regionOptions={
                    newCustomerRegions?.map((r) => ({
                      value: r.id,
                      label: r.name,
                    })) || []
                  }
                />
                <ValidationPanel
                  validationStatus={validationStatus}
                  matchedData={matchedData}
                />
              </>
            )}

            {currentStep === 2 && (
              <SurveyDetailsForm
                surveyData={surveyData}
                errors={errors}
                onChange={handleSurveyDataChange}
              />
            )}

            {currentStep === 3 && (
              <RoofInspectionSection
                roofStatus={roofStatus}
                rejectionReason={rejectionReason}
                roofInspectionPhotos={roofInspectionPhotos}
                onRoofStatusChange={(v) => {
                  setRoofStatus(v);
                  scheduleSaveRef.current?.();
                }}
                onReasonChange={(v) => {
                  setRejectionReason(v);
                  scheduleSaveRef.current?.();
                }}
                onRoofInspectionPhotosChange={(v) => {
                  setRoofInspectionPhotos(v);
                  scheduleSaveRef.current?.();
                }}
                overlayMeta={overlayMeta}
                capturedLocation={capturedLocation}
                errors={errors}
              />
            )}

            {currentStep === 4 && (
              <RoofEngineeringForm
                surveyData={surveyData}
                errors={errors}
                onChange={handleSurveyDataChange}
              />
            )}

            {currentStep === 5 && (
              <SitePhotoCapture
                roofPhotos={roofPhotos}
                meterPhotos={meterPhotos}
                lineDiagramPhotos={lineDiagramPhotos}
                onRoofPhotosChange={(v) => {
                  setRoofPhotos(v);
                  scheduleSaveRef.current?.();
                }}
                onMeterPhotosChange={(v) => {
                  setMeterPhotos(v);
                  scheduleSaveRef.current?.();
                }}
                onLineDiagramPhotosChange={(v) => {
                  setLineDiagramPhotos(v);
                  scheduleSaveRef.current?.();
                }}
                overlayMeta={overlayMeta}
                capturedLocation={capturedLocation}
                errors={errors}
              />
            )}

            {currentStep === 6 && (
              <DocumentPhotosForm
                aadharPhoto={aadharPhoto}
                bankPassbookPhoto={bankPassbookPhoto}
                electricityBillPhoto={electricityBillPhoto}
                onAadharPhotoChange={(v) => {
                  setAadharPhoto(v);
                  scheduleSaveRef.current?.();
                }}
                onBankPassbookPhotoChange={(v) => {
                  setBankPassbookPhoto(v);
                  scheduleSaveRef.current?.();
                }}
                onElectricityBillPhotoChange={(v) => {
                  setElectricityBillPhoto(v);
                  scheduleSaveRef.current?.();
                }}
                errors={errors}
              />
            )}

            {currentStep === 7 && (
              <ElectricalLogisticsForm
                surveyData={surveyData}
                errors={errors}
                onChange={handleSurveyDataChange}
              />
            )}

            {currentStep === 8 && (
              <ObstaclesSignoffForm
                surveyData={surveyData}
                errors={errors}
                onChange={handleSurveyDataChange}
                obstaclePhotos={obstaclePhotos}
                onObstaclePhotosChange={(v) => {
                  setObstaclePhotos(v);
                  scheduleSaveRef.current?.();
                }}
                overlayMeta={overlayMeta}
                capturedLocation={capturedLocation}
                tentativePanelLocationPhoto={tentativePanelLocationPhoto}
                onTentativePanelLocationPhotoChange={(v) => {
                  setTentativePanelLocationPhoto(v);
                  scheduleSaveRef.current?.();
                }}
                tentativeInverterLocationPhoto={tentativeInverterLocationPhoto}
                onTentativeInverterLocationPhotoChange={(v) => {
                  setTentativeInverterLocationPhoto(v);
                  scheduleSaveRef.current?.();
                }}
                surveyorSignaturePhoto={surveyorSignaturePhoto}
                onSurveyorSignaturePhotoChange={(v) => {
                  setSurveyorSignaturePhoto(v);
                  scheduleSaveRef.current?.();
                }}
                customerSignaturePhoto={customerSignaturePhoto}
                onCustomerSignaturePhotoChange={(v) => {
                  setCustomerSignaturePhoto(v);
                  scheduleSaveRef.current?.();
                }}
              />
            )}

            {currentStep === 9 && (
              <QRCodeLinking
                qrCode={qrCode}
                location={location}
                onQRCodeChange={(v) => {
                  setQrCode(v);
                  scheduleSaveRef.current?.();
                }}
                onCaptureLocation={handleCaptureLocation}
                errors={errors}
              />
            )}
          </div>

          {uploadProgress && (
            <div className="mt-4 p-3 rounded-lg bg-primary/10 border border-primary/20 flex items-center gap-3">
              <Icon
                name="Loader2"
                size={20}
                className="animate-spin text-primary flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {uploadProgress.totalFiles != null &&
                  uploadProgress.totalFiles > 0
                    ? `Uploading ${uploadProgress.completedFiles ?? 0} of ${uploadProgress.totalFiles} files — ${uploadProgress.label}`
                    : `Saving — ${uploadProgress.label}`}
                </p>
                <div className="mt-1.5 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{
                      width: `${
                        uploadProgress.totalFiles != null &&
                        uploadProgress.totalFiles > 0
                          ? Math.min(
                              100,
                              ((uploadProgress.completedFiles ?? 0) /
                                uploadProgress.totalFiles) *
                                100,
                            )
                          : uploadProgress.totalSteps > 0
                            ? ((uploadProgress.currentStep ??
                                uploadProgress.current ??
                                0) /
                                (uploadProgress.totalSteps ??
                                  uploadProgress.total ??
                                  1)) *
                              100
                            : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 mt-6 md:mt-8 pt-6 border-t border-border">
            <Button
              variant="outline"
              iconName="ChevronLeft"
              iconPosition="left"
              onClick={handlePrevious}
              disabled={currentStep === 1 || isSaving}
            >
              Previous
            </Button>

            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <span>
                Step {currentStep} of {steps?.length}
              </span>
              {isSaving && (
                <span className="text-primary/80 animate-pulse">Saving…</span>
              )}
              {!isSaving && lastSavedAt && (
                <span className="text-success">Data saved</span>
              )}
            </div>

            <div className="flex gap-2">
              {currentStep < steps.length ? (
                <Button
                  variant="default"
                  iconName="ChevronRight"
                  iconPosition="right"
                  onClick={handleNext}
                  loading={isLoadingNext}
                  disabled={isLoadingNext || isSaving}
                >
                  Next
                </Button>
              ) : currentStep === steps.length ? (
                <Button
                  variant="success"
                  iconName="CheckCircle2"
                  iconPosition="left"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={isSaving}
                >
                  Submit Survey
                </Button>
              ) : null}
            </div>
          </div>

          {errors?.submit && (
            <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            </div>
          )}

          {errors?.save && (
            <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-sm text-error">{errors?.save}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerSurvey;
